import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
type Consent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  date: string;
};
const STORAGE_KEY = 'kw-cookie-consent';
export const OPEN_COOKIE_SETTINGS = 'kw-open-cookie-settings';
const cardShadow = 'shadow-[0_0_0_1px_rgba(70,28,64,0.08),0_2px_4px_rgba(70,28,64,0.04),0_20px_44px_-14px_rgba(70,28,64,0.22)]';
export function CookieBanner() {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  useEffect(() => {
    const stored = readConsent();
    if (!stored) {
      const id = window.setTimeout(() => setOpen(true), 600);
      return () => window.clearTimeout(id);
    }
    setAnalytics(stored.analytics);
    setMarketing(stored.marketing);
  }, []);
  useEffect(() => {
    const reopen = () => {
      setDetails(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS, reopen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS, reopen);
  }, []);
  const save = (a: boolean, m: boolean) => {
    const consent: Consent = {
      necessary: true,
      analytics: a,
      marketing: m,
      date: new Date().toISOString()
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    } catch {
      // Storage unavailable (private mode) – consent applies for this visit only.
    }
    setAnalytics(a);
    setMarketing(m);
    setOpen(false);
    setDetails(false);
  };
  return <AnimatePresence data-id="element-158" data-mp="1cjxhzhciokym">
      {open && <motion.div role="dialog" aria-modal="false" aria-labelledby="cookie-title" aria-describedby="cookie-text" initial={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 16
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className={`fixed inset-x-3 bottom-3 z-50 rounded-[14px] bg-white p-5 text-ink sm:inset-x-auto sm:bottom-5 sm:left-5 sm:w-[400px] md:p-6 ${cardShadow}`} data-id="element-159" data-mp="1civyqplcks5t">
          <p id="cookie-title" className="text-[16px] font-medium" data-id="element-160" data-mp="2zg3rj6yv7sig">
            Cookies auf dieser Website
          </p>
          <p id="cookie-text" className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-161" data-mp="2ujn4yh7bdm7h">
            Technisch notwendige Cookies sorgen dafür, dass die Seite funktioniert. Analyse- und Marketing-Cookies setze
            ich nur mit Ihrer Einwilligung. Mehr in der{' '}
            <Link to="/cookies" className="text-ink underline underline-offset-2 hover:text-ink/70" data-id="element-162" data-mp="2gj2kzhpnxkce">
              Cookie-Richtlinie
            </Link>
            .
          </p>

          {details && <ul className="mt-4 divide-y divide-line rounded-[10px] bg-canvas px-4" data-id="element-163" data-mp="erz8so04jxpq">
              <ConsentRow label="Technisch notwendig" note="Immer aktiv" checked disabled data-id="element-164" data-mp="mays6tnl5p93" />
              <ConsentRow label="Analyse" checked={analytics} onChange={setAnalytics} data-id="element-165" data-mp="r909b91gbehx" />
              <ConsentRow label="Marketing" checked={marketing} onChange={setMarketing} data-id="element-166" data-mp="w0a0ie2nepvx" />
            </ul>}

          <div className="mt-5 grid grid-cols-2 gap-2" data-id="element-167" data-mp="10zuzgohtiy8n">
            {details ? <>
                <button type="button" onClick={() => save(false, false)} className={secondaryBtn} data-id="element-168" data-mp="3ce3t9d2wxz5">
                  Nur notwendige
                </button>
                <button type="button" onClick={() => save(analytics, marketing)} className={primaryBtn} data-id="element-169" data-mp="8cilh2pq3yqy">
                  Auswahl speichern
                </button>
              </> : <>
                <button type="button" onClick={() => save(false, false)} className={secondaryBtn} data-id="element-170" data-mp="1op7fa77ni4jx">
                  Nur notwendige
                </button>
                <button type="button" onClick={() => save(true, true)} className={primaryBtn} data-id="element-171" data-mp="1soy445dmaalt">
                  Alle akzeptieren
                </button>
              </>}
          </div>
          {!details && <button type="button" onClick={() => setDetails(true)} className="mt-3 w-full text-center text-[13px] text-muted underline underline-offset-4 transition-colors duration-150 hover:text-ink" data-id="element-172" data-mp="1mcn4pw03z7h6">
              Einstellungen anpassen
            </button>}
        </motion.div>}
    </AnimatePresence>;
}
const primaryBtn = 'h-11 rounded-[8px] bg-ink px-4 text-[14px] font-medium text-white transition-colors duration-150 hover:bg-ink/85';
const secondaryBtn = 'h-11 rounded-[8px] bg-white px-4 text-[14px] font-medium text-ink shadow-[inset_0_0_0_1px_rgba(13,13,18,0.18)] transition-colors duration-150 hover:bg-canvas';
type ConsentRowProps = {
  label: string;
  note?: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
};
function ConsentRow({
  label,
  note,
  checked,
  disabled,
  onChange
}: ConsentRowProps) {
  return <li className="flex items-center justify-between gap-4 py-3" data-id="element-173" data-mp="3ckr800mtul44">
      <span className="text-[14px] text-ink" data-id="element-174" data-mp="2ykq56fn053m0">{label}</span>
      <span className="flex items-center gap-2.5" data-id="element-175" data-mp="2yk7zytid3f1y">
        {note && <span className="text-[12px] text-muted" data-id="element-176" data-mp="33jb5sg661paz">{note}</span>}
        <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange?.(!checked)} className={`relative h-6 w-10 rounded-full transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${checked ? 'bg-ink' : 'bg-[#d9d4d8]'}`} data-id="element-177" data-mp="33lnncs80hxni">
          <span className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-150 ease-out ${checked ? 'translate-x-4' : ''}`} data-id="element-178" data-mp="25w2f86rb5q24" />
        </button>
      </span>
    </li>;
}
function readConsent(): Consent | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as Consent : null;
  } catch {
    return null;
  }
}